import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import { api } from '../App';
import 'leaflet/dist/leaflet.css';

const TIERS = ['HOT', 'HIGH', 'MEDIUM', 'LOW'];
const TIER_META = {
  HOT: { color: '#FAAB1C', label: 'Hot', desc: 'Score 90+ · call today', ring: '#0C2417' },
  HIGH: { color: '#21A84A', label: 'High', desc: 'Score 75–89 · this week', ring: '#fff' },
  MEDIUM: { color: '#7C8DA6', label: 'Medium', desc: 'Score 50–74 · nurture', ring: '#fff' },
  LOW: { color: '#CBD5E1', label: 'Low', desc: 'Below 50 · watch', ring: '#fff' },
};
const PERMIT_LABEL = (t) => (t || '').replace(/_/g, ' ');

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const lats = points.map(p => p.lat), lngs = points.map(p => p.lng);
    map.fitBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]], { padding: [40, 40], maxZoom: 13 });
  }, [points.length]); // eslint-disable-line
  return null;
}

function LeadPopup({ p }) {
  const m = TIER_META[p.tier];
  return (
    <div className="text-sm min-w-[220px]" data-testid={`map-popup-${p.id}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <h4 className="font-bold text-sm truncate">{p.name}</h4>
        <span className="text-[10px] font-black px-2 py-0.5 rounded-full text-white" style={{ background: m.color, color: p.tier === 'HOT' ? '#0C2417' : '#fff' }}>{p.tier} {p.score}</span>
      </div>
      <p className="text-xs text-gray-600">{p.address}, {p.city} {p.state}</p>
      <div className="grid grid-cols-2 gap-1 text-[11px] mt-2 text-gray-700">
        {p.permit_type && <span>Permit: <b>{PERMIT_LABEL(p.permit_type)}</b></span>}
        {p.permit_days_ago != null && <span>{p.permit_days_ago}d ago</span>}
        {p.solar_score != null && <span>Solar <b>{p.solar_score}</b></span>}
        {p.roofing_score != null && <span>Roof <b>{p.roofing_score}</b></span>}
        <span>Value <b>${Math.round(p.estimated_value / 1000)}K</b></span>
        {p.existing_solar && <span className="text-red-600 font-semibold">Existing solar</span>}
      </div>
      <Link to={`/leads/${p.id}`} className="inline-block mt-2 text-xs font-bold text-[#21A84A] hover:underline" data-testid={`map-open-lead-${p.id}`}>Open lead →</Link>
    </div>
  );
}

function TierChip({ tier, count, active, onClick }) {
  const m = TIER_META[tier];
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold transition-all ${active ? 'shadow-sm' : 'opacity-40 grayscale'}`}
      style={{ borderColor: m.color, background: active ? `${m.color}22` : 'transparent' }} data-testid={`tier-filter-${tier.toLowerCase()}`}>
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />{m.label}<span className="font-mono text-text-muted">{count}</span>
    </button>
  );
}

export default function LeadMap() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState(new Set(TIERS));
  const [heat, setHeat] = useState(true);
  const [hideSolar, setHideSolar] = useState(false);
  const [permitType, setPermitType] = useState('');

  useEffect(() => {
    api.get('/api/geo/leads/points').then(r => setData(r.data)).catch(() => setData({ points: [], density: [], tier_counts: {} })).finally(() => setLoading(false));
  }, []);

  const permitTypes = useMemo(() => [...new Set((data?.points || []).map(p => p.permit_type).filter(Boolean))].sort(), [data]);
  const visible = useMemo(() => (data?.points || []).filter(p => tiers.has(p.tier) && (!hideSolar || !p.existing_solar) && (!permitType || p.permit_type === permitType)), [data, tiers, hideSolar, permitType]);
  const hot = useMemo(() => visible.filter(p => p.tier === 'HOT' || p.tier === 'HIGH').sort((a, b) => b.score - a.score).slice(0, 12), [visible]);
  const value = visible.reduce((s, p) => s + (p.estimated_value || 0), 0);

  const toggleTier = (t) => setTiers(s => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; });
  const saveAsAlert = () => {
    const filters = { tiers: [...tiers], permit_types: permitType ? [permitType] : [], exclude_existing_solar: hideSolar, max_permit_days: 0 };
    nav('/alerts', { state: { prefill: { name: `Map view · ${[...tiers].map(t => TIER_META[t].label).join('/')}${permitType ? ' · ' + PERMIT_LABEL(permitType) : ''}`, filters } } });
  };

  if (loading) return <div className="flex items-center justify-center h-96"><i className="fa-solid fa-spinner fa-spin text-primary text-2xl mr-3" /><span className="text-text-muted">Loading map intelligence...</span></div>;

  const maxD = data?.max_density || 1;
  return (
    <div className="space-y-5 animate-fade-in" data-testid="lead-map-page">
      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: '#FAAB1C' }}>Opportunity intelligence</p>
          <h1 className="font-heading text-3xl font-black text-text-primary tracking-tight">Lead Map</h1>
          <p className="text-text-muted text-sm mt-1">Every lead pinned by opportunity tier · permit density heat underneath · {data.total_with_coords} of {data.total_leads} leads geocoded</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {TIERS.map(t => <TierChip key={t} tier={t} count={data.tier_counts?.[t] || 0} active={tiers.has(t)} onClick={() => toggleTier(t)} />)}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="map-toolbar">
        <button onClick={() => setHeat(h => !h)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${heat ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-border text-text-secondary'}`} data-testid="toggle-heat"><i className="fa-solid fa-fire mr-1.5" />Permit density heat</button>
        <button onClick={() => setHideSolar(h => !h)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${hideSolar ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-border text-text-secondary'}`} data-testid="toggle-hide-solar"><i className="fa-solid fa-ban mr-1.5" />Hide existing solar</button>
        <select value={permitType} onChange={e => setPermitType(e.target.value)} className="input-field h-8 text-xs py-0" style={{ width: 'auto' }} data-testid="permit-type-filter">
          <option value="">All permit types</option>
          {permitTypes.map(t => <option key={t} value={t}>{PERMIT_LABEL(t)}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2 text-xs text-text-muted">
          <span data-testid="visible-count"><b className="text-text-primary">{visible.length}</b> shown</span>·
          <span><b className="text-text-primary">${(value / 1000).toFixed(0)}K</b> pipeline</span>
          <button onClick={saveAsAlert} className="ml-2 text-xs font-bold px-3 py-1.5 rounded-full text-[#0C2417]" style={{ background: '#FAAB1C' }} data-testid="save-view-as-alert"><i className="fa-solid fa-bell mr-1.5" />Alert me on new matches</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className="lg:col-span-3 card overflow-hidden" style={{ height: '620px' }}>
          <MapContainer center={[39.5, -96]} zoom={4} style={{ height: '100%', width: '100%' }} data-testid="leaflet-map">
            <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
            <FitBounds points={visible} />
            {heat && (data.density || []).map((c, i) => (
              <CircleMarker key={`d${i}`} center={[c.lat, c.lng]} radius={14 + 26 * Math.sqrt(c.count / maxD)} pathOptions={{ fillColor: '#FAAB1C', color: 'transparent', fillOpacity: 0.08 + 0.3 * (c.count / maxD) }} interactive={false} />
            ))}
            {visible.map(p => {
              const m = TIER_META[p.tier];
              return (
                <CircleMarker key={p.id} center={[p.lat, p.lng]} radius={p.tier === 'HOT' ? 10 : p.tier === 'HIGH' ? 8 : 6}
                  pathOptions={{ fillColor: m.color, color: p.existing_solar ? '#DC2626' : m.ring, weight: p.tier === 'HOT' ? 2.5 : 1.5, fillOpacity: p.tier === 'LOW' ? 0.55 : 0.9 }}>
                  <Popup><LeadPopup p={p} /></Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>

        <div className="space-y-4">
          <div className="card p-5" data-testid="tier-legend">
            <h3 className="font-heading text-sm font-bold text-text-primary mb-3 flex items-center gap-2"><i className="fa-solid fa-layer-group text-primary text-xs" />Opportunity tiers</h3>
            <div className="space-y-2.5">
              {TIERS.map(t => (
                <div key={t} className="flex items-center gap-3">
                  <span className="w-4 h-4 rounded-full flex-shrink-0 border-2" style={{ background: TIER_META[t].color, borderColor: TIER_META[t].ring }} />
                  <div className="flex-1 min-w-0"><p className="text-xs font-bold text-text-primary">{TIER_META[t].label} <span className="font-mono text-text-muted font-normal">· {data.tier_counts?.[t] || 0}</span></p><p className="text-[10px] text-text-muted">{TIER_META[t].desc}</p></div>
                </div>
              ))}
              <div className="flex items-center gap-3 pt-2 border-t border-border"><span className="w-4 h-4 rounded-full flex-shrink-0 border-2 border-red-600 bg-white" /><p className="text-[10px] text-text-muted">Red ring = existing solar (competitor install)</p></div>
              <div className="flex items-center gap-3"><span className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: '#FAAB1C55' }} /><p className="text-[10px] text-text-muted">Amber glow = permit density (more permits, hotter block)</p></div>
            </div>
          </div>

          <div className="card p-5" data-testid="hot-list">
            <h3 className="font-heading text-sm font-bold text-text-primary mb-3 flex items-center gap-2"><i className="fa-solid fa-fire text-secondary text-xs" />Call list — hot &amp; high</h3>
            {hot.length === 0 ? <p className="text-xs text-text-muted">No hot or high-tier leads in this view. Run a Permit Scanner sweep to add fresh ones.</p> : (
              <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1">
                {hot.map(p => (
                  <Link key={p.id} to={`/leads/${p.id}`} className="flex items-center justify-between p-2.5 rounded-xl hover:bg-surface-alt transition-all" data-testid={`hot-lead-${p.id}`}>
                    <div className="min-w-0"><p className="text-xs font-semibold text-text-primary truncate">{p.name}</p><p className="text-[10px] text-text-muted truncate">{p.city}, {p.state} · {PERMIT_LABEL(p.permit_type) || 'no permit'}</p></div>
                    <span className="text-[10px] font-black px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: TIER_META[p.tier].color, color: p.tier === 'HOT' ? '#0C2417' : '#fff' }}>{p.score}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
